import { Box, Flex, LinkBox } from "@biblioteksentralen/react";
import { isCampaignSeason, isProduction, ResolvedSommerlesBannerData } from "@libry-content/common";
import { ComponentProps, ReactElement } from "react";
import { logClick } from "../../utils/analytics/Plausible";
import { useScreenIsSmall } from "../../utils/hooks/useScreenIsSmall";
import { MissingCampaignTemplateContentBanner } from "../campaigns/MissingCampaignTemplateContentBanner";
import { Edit } from "../editInSanity/EditInSanity";
import { BannerContentPrelude } from "./BannerContentPrelude";
import { BannerContentUnderway } from "./BannerContentUnderway";
import { Limelight } from "next/font/google";
import { getContainerQuery } from "../site/banners/promotedItems/utils";

const limelightFont = Limelight({ subsets: ["latin"], weight: "400" });

export type SommerlesBannerProps = {
  sommerles: ResolvedSommerlesBannerData | undefined;
  onLandingPage?: boolean;
};

export const SommerlesBanner = (props: SommerlesBannerProps) => {
  const { sommerles, onLandingPage = false } = props;
  const smallScreen = useScreenIsSmall();
  const isSeason = sommerles ? isCampaignSeason(sommerles, { applyTeaserStartDate: false }) : false;
  const templateContent = sommerles?.templateContent;
  const missingContent = !templateContent?.backgroundImage?.asset;

  if (missingContent) console.error("Missing necessary assets to display sommerles banner");

  if (missingContent && !isProduction) {
    return (
      <MissingCampaignTemplateContentBanner
        campaignName="Sommerles"
        templatePath="/cms/structure/campaigns;sommerlesSettings"
      />
    );
  }

  if (!templateContent) {
    return null;
  }

  const BannerContent = getBannerContent(isSeason, onLandingPage);

  const minHeight = getMinHeight(isSeason, onLandingPage);

  return (
    <LinkBox
      onClick={() => logClick("Sommerlesbanner")}
      as="aside"
      className={limelightFont.className}
      textDecoration="none"
      position="relative"
      role="group"
      width="100%"
      height="100%"
      sx={{
        "h1,h2": limelightFont.style,
        containerType: "inline-size",
      }}
    >
      <Box
        width="100%"
        height="100%"
        borderRadius={{ base: 0, md: "0.5rem" }}
        sx={getContainerQuery({
          base: { overflow: "hidden" },
          md: { overflow: "visible" },
        })}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          position="relative"
          padding="0"
          minHeight={minHeight}
          height="100%"
        >
          <BannerContent sommerles={sommerles} smallScreen={smallScreen} onLandingPage={onLandingPage} />
        </Flex>
        <Box marginTop="0 !important" zIndex={2}>
          {sommerles && <Edit doc={sommerles} margin="auto" />}
        </Box>
      </Box>
    </LinkBox>
  );
};

const getBannerContent = (
  isSeason: boolean,
  onLandingPage: boolean
): ((props: ComponentProps<typeof BannerContentUnderway>) => ReactElement | null) => {
  if (onLandingPage && !isSeason) return BannerContentPrelude;
  return BannerContentUnderway;
};

/**
 * Set min-height to hard-coded empirical values so that the banner
 * is not short until the background has loaded
 */
const getMinHeight = (isSeason: boolean, onLandingPage: boolean) => {
  if (isSeason || !onLandingPage) return { base: "14.8rem", sm: "15.5rem", md: "21.4rem", lg: "21.8rem" };
  return { base: "15.3rem", sm: "16.6rem", md: "20.7rem" };
};
